import axios from "axios";
import { bearerTokenForRequest } from "@/lib/apiAuthToken";
import { isBackendAuthEnabled } from "@/lib/authApi";
import { isCognitoConfigured } from "@/lib/cognitoPool";
import { API_KEY } from "@/lib/env";
import { notifyPermission403 } from "@/lib/permission403";
import { silentRefreshBackendSession } from "@/lib/sessionRefresh";
import {
  notifyUnauthorized401,
  shouldHandleUnauthorized401,
} from "@/lib/unauthorized401";

function isPublicAuthRequest(url) {
  const path = String(url || "");
  return (
    path.includes("/api/auth/login")
    || path.includes("/api/auth/config")
    || path.includes("/api/auth/complete")
    || path.includes("/api/auth/callback")
    || path.includes("/api/auth/logout")
    || path.includes("/api/auth/refresh")
    || path.includes("/api/auth/new-password")
    || path.includes("/api/auth/forgot-password")
    || path.includes("/api/auth/confirm-forgot-password")
  );
}

// authapi: access token. Other microservices: ID token until API Gateway (Phase 4).
axios.interceptors.request.use((config) => {
  if (isPublicAuthRequest(config.url)) {
    return config;
  }
  const token = bearerTokenForRequest(config.url);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    if (shouldHandleUnauthorized401(error) && config && !config.skipAuthRetry) {
      if (isBackendAuthEnabled()) {
        const refreshed = await silentRefreshBackendSession();
        if (refreshed && !config.__authRetried) {
          config.__authRetried = true;
          const token = bearerTokenForRequest(config.url);
          if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
          }
          return axios(config);
        }
      }
      notifyUnauthorized401();
    }
    if (error?.response?.status === 403) {
      notifyPermission403();
    }
    return Promise.reject(error);
  },
);

// /api/* (integrations) still use x-api-key; SPA only calls /backend/* with Cognito JWT.
if (API_KEY) {
  axios.defaults.headers.common["x-api-key"] = API_KEY;
}

if (
  process.env.NODE_ENV === "development" &&
  !isCognitoConfigured &&
  !API_KEY
) {
  console.warn(
    "[halo] Set REACT_APP_COGNITO_USER_POOL_ID + REACT_APP_COGNITO_CLIENT_ID (or REACT_APP_API_KEY for legacy /backend).",
  );
}
